import React from 'react';
import { Typography } from 'antd';
import { isMarkdownHeadingLine, matchMarkdownHeadingLine } from './markdownHeading.js';
import { consumeOrderedListBlock, normalizeOrderedListNumbers } from './markdownOrderedList.js';
import { renderCitationNodes } from './CitationLink.jsx';

const { Paragraph, Title } = Typography;

const conditionalValue = (condition, whenTrue, whenFalse) => (
  condition ? whenTrue : whenFalse
);

function splitTableRow(row) {
  const rawCells = String(row || '').trim().replace(/^\|/, '').replace(/\|$/, '').split('|');
  return rawCells.map((cell) => cell.trim());
}

function isTableDelimiterLine(line) {
  const cells = splitTableRow(line);
  if (!cells.length) {
    return false;
  }
  return cells.every((cell) => /^:?-{3,}:?$/.test(cell));
}

const isHorizontalRuleLine = (line) => /^\s{0,3}([-*_])(?:\s*\1){2,}\s*$/.test(line);

function renderInlineMarkdown(text, keyPrefix, textStyle = {}) {
  const rawText = String(text ?? '');
  const parts = rawText.split(/(`[^`]+`|\*\*[^*]+\*\*)/g).filter((part) => part !== '');
  return parts.map((part, index) => {
    const key = `${keyPrefix}-inline-${index}`;
    if ([part.startsWith('`'), part.endsWith('`'), part.length >= 2].every(Boolean)) {
      return <code key={key} className="flare-markdown-inline-code">{part.slice(1, -1)}</code>;
    }
    if ([part.startsWith('**'), part.endsWith('**'), part.length >= 4].every(Boolean)) {
      return <strong key={key} style={textStyle}>{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={key}>{part}</React.Fragment>;
  });
}

const pushParagraph = ({ blocks, keyPrefix, paragraphLines, textStyle }) => {
  if (!paragraphLines.length) {
    return;
  }
  const blockKey = `${keyPrefix}-p-${blocks.length}`;
  blocks.push(
    <Paragraph key={blockKey} style={{ ...textStyle, marginBottom: 0, whiteSpace: 'pre-wrap' }}>
      {renderInlineMarkdown(paragraphLines.join('\n'), blockKey, textStyle)}
    </Paragraph>
  );
};

const consumeCodeBlock = ({ blocks, index, keyPrefix, lines }) => {
  const codeLines = [];
  let nextIndex = index + 1;
  while ([nextIndex < lines.length, !/^```/.test(lines[nextIndex])].every(Boolean)) {
    codeLines.push(lines[nextIndex]);
    nextIndex += 1;
  }
  nextIndex += conditionalValue(nextIndex < lines.length, 1, 0);
  blocks.push(
    <pre key={`${keyPrefix}-code-${blocks.length}`} className="flare-markdown-code-block">
      <code>{codeLines.join('\n')}</code>
    </pre>
  );
  return nextIndex;
};

const consumeHeadingBlock = ({ blocks, index, keyPrefix, lines, textStyle }) => {
  const headingMatch = matchMarkdownHeadingLine(lines[index]);
  const level = headingMatch[1].length;
  const blockKey = `${keyPrefix}-h-${blocks.length}`;
  blocks.push(
    <Title
      className="flare-markdown-heading"
      data-markdown-level={level}
      key={blockKey}
      level={Math.min(level + 2, 5)}
    >
      {renderInlineMarkdown(headingMatch[2], blockKey, textStyle)}
    </Title>
  );
  return index + 1;
};

const consumeHorizontalRule = ({ blocks, index, keyPrefix }) => {
  blocks.push(<hr aria-hidden="true" className="flare-markdown-divider" key={`${keyPrefix}-hr-${blocks.length}`} />);
  return index + 1;
};

const patchLinePresentation = (line) => {
  const presentations = new Map([
    ['-', { className: 'is-removed', color: '#a61d24', background: '#fff1f0' }],
    ['+', { className: 'is-added', color: '#237804', background: '#f6ffed' }],
  ]);
  const marker = line.slice(0, 1);
  const presentation = presentations.get(marker);
  return presentation
    ? { ...presentation, content: line.slice(2) }
    : { className: 'is-context', color: '#475569', background: '#f8fafc', content: line };
};

const consumePatchPreviewBlock = ({ blocks, index, keyPrefix, lines, textStyle }) => {
  const changeLines = [];
  let nextIndex = index + 1;
  while ([nextIndex < lines.length, Boolean(String(lines[nextIndex] ?? '').trim()), !/^@@\s+/.test(lines[nextIndex])].every(Boolean)) {
    changeLines.push(lines[nextIndex]);
    nextIndex += 1;
  }
  const blockKey = `${keyPrefix}-patch-${blocks.length}`;
  blocks.push(
    <div className="flare-markdown-patch-preview" key={blockKey}>
      <div className="flare-markdown-patch-heading">
        {renderInlineMarkdown(lines[index].replace(/^@@\s+/, ''), `${blockKey}-heading`, textStyle)}
      </div>
      {changeLines.map((line, lineIndex) => {
        const presentation = patchLinePresentation(line);
        return (
          <div
            className={`flare-markdown-patch-line ${presentation.className}`}
            key={`${blockKey}-line-${lineIndex}`}
            style={{ background: presentation.background, color: presentation.color, whiteSpace: 'pre-wrap' }}
          >
            {renderInlineMarkdown(presentation.content, `${blockKey}-line-${lineIndex}`, textStyle)}
          </div>
        );
      })}
    </div>,
  );
  return nextIndex;
};

const consumeUnorderedListBlock = ({ blocks, index, keyPrefix, lines, textStyle }) => {
  const items = [];
  let nextIndex = index;
  while ([nextIndex < lines.length, /^[-*]\s+/.test(lines[nextIndex])].every(Boolean)) {
    items.push(lines[nextIndex].replace(/^[-*]\s+/, ''));
    nextIndex += 1;
  }
  blocks.push(
    <ul key={`${keyPrefix}-ul-${blocks.length}`} className="flare-markdown-list">
      {items.map((item, itemIndex) => (
        <li key={`${keyPrefix}-ul-item-${itemIndex}`} style={textStyle}>
          {renderInlineMarkdown(item, `${keyPrefix}-ul-item-${itemIndex}`, textStyle)}
        </li>
      ))}
    </ul>
  );
  return nextIndex;
};

const consumeQuoteBlock = ({ blocks, index, keyPrefix, lines, textStyle }) => {
  const quoteLines = [];
  let nextIndex = index;
  while ([nextIndex < lines.length, /^>\s?/.test(lines[nextIndex])].every(Boolean)) {
    quoteLines.push(lines[nextIndex].replace(/^>\s?/, ''));
    nextIndex += 1;
  }
  blocks.push(
    <blockquote key={`${keyPrefix}-quote-${blocks.length}`} className="flare-markdown-blockquote">
      {quoteLines.map((line, lineIndex) => (
        <Paragraph key={`${keyPrefix}-quote-line-${lineIndex}`} style={{ ...textStyle, marginBottom: 0 }}>
          {renderInlineMarkdown(line, `${keyPrefix}-quote-line-${lineIndex}`, textStyle)}
        </Paragraph>
      ))}
    </blockquote>
  );
  return nextIndex;
};

const consumeTableBlock = ({ blocks, index, keyPrefix, lines, textStyle }) => {
  const headerCells = splitTableRow(lines[index]);
  const rows = [];
  let nextIndex = index + 2;
  while ([nextIndex < lines.length, lines[nextIndex].includes('|'), lines[nextIndex].trim()].every(Boolean)) {
    rows.push(splitTableRow(lines[nextIndex]));
    nextIndex += 1;
  }
  blocks.push(
    <div key={`${keyPrefix}-table-wrap-${blocks.length}`} className="flare-markdown-table-wrap">
      <table className="flare-markdown-table">
        <thead><tr>{headerCells.map((cell, cellIndex) => (
          <th key={`${keyPrefix}-th-${cellIndex}`} style={textStyle}>
            {renderInlineMarkdown(cell, `${keyPrefix}-th-${cellIndex}`, textStyle)}
          </th>
        ))}</tr></thead>
        <tbody>{rows.map((row, rowIndex) => (
          <tr key={`${keyPrefix}-tr-${rowIndex}`}>{headerCells.map((cell, cellIndex) => (
            <td key={`${keyPrefix}-td-${rowIndex}-${cellIndex}`} style={textStyle}>
              {renderInlineMarkdown(row[cellIndex] ?? '', `${keyPrefix}-td-${rowIndex}-${cellIndex}`, textStyle)}
            </td>
          ))}</tr>
        ))}</tbody>
      </table>
    </div>
  );
  return nextIndex;
};

const consumeOrderedBlock = ({ blocks, index, keyPrefix, lines, textStyle }) => {
  const ordered = consumeOrderedListBlock(lines, index);
  blocks.push(
    <ol key={`${keyPrefix}-ol-${blocks.length}`} className="flare-markdown-list" start={ordered.firstNumber}>
      {ordered.items.map((item, itemIndex) => (
        <li key={`${keyPrefix}-ol-item-${itemIndex}`} style={textStyle}>
          {renderInlineMarkdown(item, `${keyPrefix}-ol-item-${itemIndex}`, textStyle)}
        </li>
      ))}
    </ol>
  );
  return ordered.nextIndex;
};

const isTableStart = (lines, index) => [
  lines[index].includes('|'),
  isTableDelimiterLine(lines[index + 1] ?? ''),
].every(Boolean);

const isParagraphLine = (lines, index) => (
  index < lines.length
  && [
    Boolean(lines[index].trim()),
    !/^```/.test(lines[index]),
    !/^@@\s+/.test(lines[index]),
    !isMarkdownHeadingLine(lines[index]),
    !/^[-*]\s+/.test(lines[index]),
    !/^>\s?/.test(lines[index]),
    !isTableStart(lines, index),
    !/^\d+\.\s+/.test(lines[index]),
  ].every(Boolean)
);

const consumeParagraphBlock = ({ blocks, index, keyPrefix, lines, textStyle }) => {
  const paragraphLines = [];
  let nextIndex = index;
  while (isParagraphLine(lines, nextIndex)) {
    paragraphLines.push(lines[nextIndex]);
    nextIndex += 1;
  }
  pushParagraph({ blocks, keyPrefix, paragraphLines, textStyle });
  return nextIndex;
};

const BLOCK_RULES = [
  [(lines, index) => /^```/.test(lines[index]), consumeCodeBlock],
  [(lines, index) => !lines[index].trim(), ({ index }) => index + 1],
  [(lines, index) => /^@@\s+/.test(lines[index]), consumePatchPreviewBlock],
  [(lines, index) => isHorizontalRuleLine(lines[index]), consumeHorizontalRule],
  [(lines, index) => Boolean(matchMarkdownHeadingLine(lines[index])), consumeHeadingBlock],
  [(lines, index) => /^[-*]\s+/.test(lines[index]), consumeUnorderedListBlock],
  [(lines, index) => /^>\s?/.test(lines[index]), consumeQuoteBlock],
  [isTableStart, consumeTableBlock],
  [(lines, index) => /^\d+\.\s+/.test(lines[index]), consumeOrderedBlock],
];

const resolveBlockConsumer = (lines, index) => (
  BLOCK_RULES.find(([matches]) => matches(lines, index))?.[1] ?? consumeParagraphBlock
);

export function renderMarkdownBlocks(content, keyPrefix, textStyle = {}, citationOptions = {}) {
  const normalized = normalizeOrderedListNumbers(content).replace(/\r\n/g, '\n');
  if (!normalized.trim()) {
    return null;
  }
  const lines = normalized.split('\n');
  const blocks = [];
  let index = 0;
  while (index < lines.length) {
    const consumeBlock = resolveBlockConsumer(lines, index);
    index = consumeBlock({ blocks, index, keyPrefix, lines, textStyle });
  }
  return renderCitationNodes(<div className="flare-markdown-content">{blocks}</div>, citationOptions);
}
